/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { NgClass, NgStyle } from "@angular/common";
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	TemplateRef
} from "@angular/core";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { ECharts, EChartsOption } from "echarts";
import { NgxEchartsModule } from "ngx-echarts";
import { NgxTippyModule } from "ngx-tippy-wrapper";
import { takeUntil, tap } from "rxjs";
import { CategoriesSource } from "../../models/ui/categories-source.model";
import { Chart, ChartPattern, ChartType } from "../../models/ui/charts.model";
import { TooltipLabel } from "../../models/ui/tooltip-label.model";
import { ChartService } from "../../services/chart.service";
import { AppStateService } from "../../services/state/app-state.service";
import { isChartDataPresent } from "../../utils/echarts/chart-utils";
import { ButtonComponent } from "../button/button.component";
import { SpinnerComponent } from "../spinner/spinner.component";

@Component({
	selector: "globalictcc-chart",
	standalone: true,
	imports: [
		NgxEchartsModule,
		NgxTippyModule,
		ButtonComponent,
		NgClass,
		NgStyle,
		MatProgressSpinnerModule,
		SpinnerComponent
	],
	templateUrl: "./chart.component.html",
	styleUrl: "./chart.component.scss"
})
export class ChartComponent implements OnChanges, OnInit {
	@Input() public chartType: ChartType;
	@Input() public data?: Chart;
	@Input() public configOverrides: EChartsOption = {};
	@Input() public hasError?: boolean = false;
	@Input() public isLoading: boolean = false;
	@Input() public isGradient: boolean = false;
	@Input() public isGradientInverted: boolean = false;
	@Input() public labelPosition?: "outside" | "inside";
	@Input() public patterns: ChartPattern[] = [];
	@Input() public colors: string[] = [];
	@Input() public isTitleVisible: boolean = true;
	@Input() public isLegendVisible: boolean = false;
	@Input() public titleAlignment: "left" | "center" | "right" = "left";
	@Input() public titleSize: "small" | "large" = "large";
	@Input() public tooltipLabel: TooltipLabel = "value";
	@Input() public categoriesSource?: CategoriesSource;
	@Input() public tooltip: string | TemplateRef<unknown> = "";
	@Input() public height: string = "400px";
	@Input() public useGrouping: boolean = false;

	@Output() public handleChartRefresh = new EventEmitter<void>();
	@Output() public handleChartInit = new EventEmitter<ECharts>();
	@Output() public handleChartClick = new EventEmitter<unknown>();

	public instance?: ECharts;
	public chartOptions: EChartsOption = {};
	public readonly emptyOptions: EChartsOption = {};
	public title?: string;
	public isDataPresent: boolean = false;

	constructor(
		private _chartService: ChartService,
		private _appStateService: AppStateService
	) {}

	ngOnInit() {
		this._appStateService.isAppAccessible$
			.pipe(
				tap({ next: () => this._updateChartOptions() }),
				takeUntil(this._appStateService.isAppDestroyed$)
			)
			.subscribe();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes["configOverrides"] || changes["data"] || changes["useGrouping"]) {
			this._updateChartOptions();
		}
	}

	/**
	 * Updates the chart options based on the current data and configuration.
	 *
	 * This method checks if the `data` property is available. If not, it exits early.
	 * If `data` is present, it sets the chart's title and updates the chart options
	 * using the `_chartService`.
	 *
	 * @private
	 */
	private _updateChartOptions() {
		this.isDataPresent = isChartDataPresent(this.data, this.chartType);

		if (!this.isDataPresent) return;

		this.title = this.data?.title;

		this.chartOptions = this._chartService.getChartOptions(
			this.chartType,
			this.data,
			this.configOverrides,
			this.isGradient,
			this.isGradientInverted,
			this.colors,
			this.patterns,
			this.isLegendVisible,
			this.tooltipLabel,
			this.categoriesSource,
			this.labelPosition,
			this.useGrouping
		);
	}

	/**
	 * Event handler for chart initialization.
	 *
	 * This method is triggered when the chart is initialized and emits the
	 * initialized ECharts instance through the `chartInit` event emitter.
	 *
	 * @param echartsInstance - The initialized ECharts instance.
	 */
	public onChartInit(echartsInstance: ECharts) {
		this.instance = echartsInstance;
		this.handleChartInit.emit(this.instance);
	}
}
